import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useIsClient } from '@utils/common';
import { useDesktopOrWider } from '@utils/mediaQuery';
import { useViewedProducts } from '@hook/useViewedProducts';
import { NavLayout } from '@outlet/layouts/NavLayout';
import { ViewportLazy } from '@components/ViewportLazy';
import { ProductDetailsBreadcrumbsContainer } from '@containers/product/ProductDetailsBreadcrumbs';
import { ProductDetailsInfoContainer } from '@containers/product/ProductDetailsInfo';
import { ProductDetailsSellerInfoContainer } from '@containers/product/ProductDetailsSelelrInfo';
import { ProductDetailsSafePaymentHelpLink } from '@components/product/ProductDetailsSafePaymentHelpLink';
import { ProductDetailsPropertiesContainer } from '@containers/product/ProductDetailsProperties';
import { ProductDetailsSellerMethodsContainer } from '@containers/product/ProductDetailsSellerMethods';
import { ProductDetailsReviewsContainer } from '@containers/product/ProductDetailsReviews';
import { ProductDetailsViewedProducts } from '@components/product/ProductDetailsViewedProducts';
import { ProductDetailsSimilarProductsContainer } from '@containers/product/ProductDetailsSimilarProducts';
import { ProductDetailsTopProductsContainer } from '@containers/product/ProductDetailsTopProducts';
import { UserComplaintContainer } from '@containers/product/ProductDetailsUserComplaint';
import { ProductDetailsMetaContainer } from '@containers/product/ProductDetailsMeta';
import { ProductDetailsAnalyticsContainer } from '@containers/ProductDetailsAnalytics';
import { ProductDetailsBottomBarContainer } from '@containers/product/ProductDetailsBottomBar';

import { useProductDetailsOutletPageQuery } from './gql/graphql.v3.generated';
import { useProductDetailsOutletPageV5Query } from './gql/graphql.v5.generated';

import s from './ProductDetailsPage.sss';

interface RouteParams {
  namespace: string,
  category: string,
  productId: string,
  slug: string,
}

export const ProductDetailsPage = () => {
  const params = useParams<RouteParams>();

  const isClient = useIsClient();
  const isDesktop = useDesktopOrWider();
  const { addViewedProductId } = useViewedProducts();

  const productId = Number(params.productId);

  const { data: productDetailsData } = useProductDetailsOutletPageQuery({
    variables: {
      productId,
      catalogSlug: params.category,
    },
  });

  useProductDetailsOutletPageV5Query({
    variables: {
      productId,
    },
    context: { v5: true },
  });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [productId]);

  useEffect(
    () => addViewedProductId(productId),
    [addViewedProductId, productId],
  );

  return (
    <>
      <ProductDetailsMetaContainer productId={productId} />

      <NavLayout className={s.root}>
        <ProductDetailsBreadcrumbsContainer
          className={s.breadcrumbs}
          catalog={productDetailsData?.catalog}
        />

        <div className={s.content}>
          <main className={s.main}>
            <ProductDetailsInfoContainer
              product={productDetailsData?.productDetails}
              catalog={productDetailsData?.catalog}
            />

            <ProductDetailsPropertiesContainer
              className={s.properties}
              product={productDetailsData?.productDetails}
              catalog={productDetailsData?.catalog}
              catalogSlug={params.category}
            />

            <ViewportLazy rootMargin='100px'>
              <ProductDetailsSellerMethodsContainer
                className={s.sellerMethods}
                productId={productId}
              />
            </ViewportLazy>

            <ViewportLazy>
              <ProductDetailsReviewsContainer
                className={s.reviews}
                productId={productId}
              />
            </ViewportLazy>
          </main>

          <aside className={s.aside}>
            <ViewportLazy>
              <ProductDetailsSellerInfoContainer product={productDetailsData?.productDetails} />
              <ProductDetailsSafePaymentHelpLink className={s.safePaymentLink} />
            </ViewportLazy>
          </aside>

          <div className={s.additional}>
            <div className={s.complaint}>
              <ViewportLazy>
                <UserComplaintContainer product={productDetailsData?.productDetails} />
              </ViewportLazy>
            </div>

            <ViewportLazy>
              <ProductDetailsViewedProducts
                className={s.viewedProducts}
                productId={productId}
              />
            </ViewportLazy>

            <div id='similar-products'>
              <ViewportLazy>
                <ProductDetailsSimilarProductsContainer
                  className={s.similarProducts}
                  productId={productId}
                />
              </ViewportLazy>
            </div>

            <ViewportLazy>
              <ProductDetailsTopProductsContainer
                className={s.topProducts}
                productId={productId}
              />
            </ViewportLazy>
          </div>
        </div>

        {isClient && !isDesktop && (
          <ProductDetailsBottomBarContainer
            className={s.bottomBar}
            product={productDetailsData?.productDetails}
          />
        )}
      </NavLayout>

      <ProductDetailsAnalyticsContainer
        product={productDetailsData?.productDetails}
        catalog={productDetailsData?.catalog}
        withIncrViews={true}
      />
    </>
  );
};

export default ProductDetailsPage;
